/**
 * RatingStore — shared cache of per-song star ratings.
 * One network GET per song (deduped); a PUT on any surface updates every
 * mounted <SongStarRating/> for that song. Demo tracks (no DB row) fall back
 * to a browser-local rating keyed by a stable demo key.
 */
import React, { createContext, useContext, useEffect, useSyncExternalStore, useRef } from "react";
import { api } from "@/lib/api";

const EMPTY = { my_rating: null, average_rating: null, rating_count: 0 };
const LOCAL_KEY = "ss_local_demo_ratings_v1";

function readLocal() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "{}"); } catch { return {}; }
}
function writeLocal(key, value) {
  const all = readLocal();
  all[key] = value;
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(all)); } catch { /* ignore */ }
}

function createStore() {
  const state = new Map();
  const listeners = new Map();
  const inflight = new Map();
  const seeded = new Set();
  const emit = (key) => { const fns = listeners.get(key); if (fns) fns.forEach((fn) => fn()); };
  return {
    get(key) { return state.get(key) || EMPTY; },
    subscribe(key, fn) {
      let fns = listeners.get(key);
      if (!fns) { fns = new Set(); listeners.set(key, fns); }
      fns.add(fn);
      return () => { fns.delete(fn); if (fns.size === 0) listeners.delete(key); };
    },
    seed(key, snapshot) { if (!snapshot) return; state.set(key, snapshot); seeded.add(key); emit(key); },
    seedLocal(key) {
      if (seeded.has(key)) return;
      const mine = readLocal()[key] || null;
      state.set(key, { ...EMPTY, my_rating: mine, local: true });
      seeded.add(key); emit(key);
    },
    async ensureFetched(key) {
      if (seeded.has(key)) return;
      if (inflight.has(key)) { await inflight.get(key); return; }
      const p = (async () => {
        try {
          const { data } = await api.get(`/v1/songs/${encodeURIComponent(key)}/rating`);
          state.set(key, data); seeded.add(key); emit(key);
        } catch (e) {
          if (e?.response?.status === 404) this.seedLocal(key);
        } finally { inflight.delete(key); }
      })();
      inflight.set(key, p);
      await p;
    },
    async rate(key, value) {
      const cur = state.get(key) || EMPTY;
      if (cur.local) {
        writeLocal(key, value);
        state.set(key, { ...cur, my_rating: value }); emit(key);
        return { ok: true, local: true };
      }
      // optimistic
      state.set(key, { ...cur, my_rating: value }); emit(key);
      try {
        const { data } = await api.put(`/v1/songs/${encodeURIComponent(key)}/rating`, { rating_value: value });
        state.set(key, data); seeded.add(key); emit(key);
        return { ok: true };
      } catch (e) {
        if (e?.response?.status === 404) {
          writeLocal(key, value);
          state.set(key, { ...EMPTY, my_rating: value, local: true }); seeded.add(key); emit(key);
          return { ok: true, local: true };
        }
        state.set(key, cur); emit(key);
        return { ok: false, error: e };
      }
    },
  };
}

const StoreCtx = createContext(null);
export function RatingStoreProvider({ children }) {
  const ref = useRef();
  if (!ref.current) ref.current = createStore();
  return <StoreCtx.Provider value={ref.current}>{children}</StoreCtx.Provider>;
}

let _default = null;
function useStore() {
  const ctx = useContext(StoreCtx);
  if (ctx) return ctx;
  if (!_default) _default = createStore();
  return _default;
}

export function useSongRating(key, seed, { local = false } = {}) {
  const store = useStore();
  useEffect(() => { if (seed && key) store.seed(key, seed); }, [key, seed, store]);
  useEffect(() => { if (!key) return; if (local) store.seedLocal(key); else store.ensureFetched(key); }, [key, local, store]);
  const subscribe = (fn) => (key ? store.subscribe(key, fn) : () => {});
  const getSnapshot = () => (key ? store.get(key) : EMPTY);
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [value, (v) => store.rate(key, v)];
}
